import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { User, Package, Check, ChevronRight } from 'lucide-react';
import AppleHeader from '../components/AppleHeader';
import { loadLastCrearAnuncioState, mergeLastCrearAnuncioState } from '../utils/crearAnuncioStorage';

export const SeleccionarTipoImagenAnuncio: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(
    () => idsParam.split(',').map((s) => s.trim()).filter(Boolean).filter((s) => s !== '0'),
    [idsParam]
  );
  const modo = (params.get('modo') as 'rapido' | 'personalizado') || 'personalizado';

  type TipoKey = 'modelo' | 'producto';
  const allowedTipos: TipoKey[] = ['modelo', 'producto'];
  const tipoParam = params.get('composicion') as TipoKey | null;
  const savedStateRef = useRef(loadLastCrearAnuncioState());
  const initialTipo: TipoKey = useMemo(() => {
    if (tipoParam && allowedTipos.includes(tipoParam)) return tipoParam;
    const saved = savedStateRef.current?.composicion as TipoKey | undefined;
    if (saved && allowedTipos.includes(saved)) return saved;
    return 'modelo';
  }, [tipoParam]);
  const [tipo, setTipo] = useState<TipoKey>(initialTipo);

  const tipos: Array<{
    key: TipoKey;
    title: string;
    description: string;
    Icon: React.ComponentType<any>;
    colorBg: string;
    colorText: string;
  }> = [
    {
      key: 'modelo',
      title: 'Modelo con producto',
      description: 'Generá una imagen de una persona utilizando el/los productos seleccionados.',
      Icon: User,
      colorBg: 'bg-violet-50',
      colorText: 'text-violet-600',
    },
    {
      key: 'producto',
      title: 'Solo producto',
      description: 'Generá una imagen centrada en el/los productos seleccionados.',
      Icon: Package,
      colorBg: 'bg-blue-50',
      colorText: 'text-blue-600',
    },
  ];

  const handleSelect = (key: TipoKey) => setTipo(key);

  // Scroll to top on mount (iOS Safari fix)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    mergeLastCrearAnuncioState({
      modo,
      selectedVariantIds: ids,
      composicion: tipo,
    });
  }, [ids, modo, tipo]);

  const handleContinue = () => {
    if (!tipo) return;
    const qp = new URLSearchParams();
    qp.set('ids', ids.join(','));
    qp.set('modo', modo);
    qp.set('composicion', tipo);
    // En modo personalizado, el siguiente paso es seleccionar formato
    navigate(`/redes-sociales/crear-anuncio/seleccionar-formato?${qp.toString()}`);
  };

  return (
    <div className="p-4 sm:p-8 sm:pb-0 max-w-4xl mx-auto min-h-[100dvh] md:min-h-screen bg-white sm:bg-transparent">
      {/* Header + progress */}
      <div className="sm:bg-white sm:rounded-2xl sm:shadow-sm p-0 sm:p-6">
        <AppleHeader title="Seleccionar tipo de imagen" />
        <div className="mt-4">
          <div className="w-full h-1 bg-gray-200 rounded-full">
            <div className="h-1 bg-blue-500 rounded-full" style={{ width: '50%' }} />
          </div>
        </div>

        {/* Tipos */}
        <div className="mt-6 grid grid-cols-1 gap-3">
          {tipos.map(({ key, title, description, Icon, colorBg, colorText }) => {
            const selected = tipo === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleSelect(key)}
                className={`w-full text-left border ${selected ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200'} rounded-2xl p-4 flex items-start gap-4 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
              >
                <div className={`shrink-0 ${colorBg} p-2 rounded-md`}>
                  <Icon className={`w-6 h-6 ${colorText}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900">{title}</h2>
                    {selected && (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white">
                        <Check className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{description}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer actions (desktop) */}
        <div className="mt-6 hidden sm:flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Volver
          </button>

          <button
            type="button"
            disabled={!tipo}
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${!tipo ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
            onClick={handleContinue}
          >
            Continuar
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mobile spacer for fixed bottom bar */}
      <div className="sm:hidden" style={{ height: 'calc(env(safe-area-inset-bottom, 0px) + 64px)' }} />

      {/* Mobile bottom bar */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Volver
          </button>
          <button
            type="button"
            disabled={!tipo}
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${!tipo ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
            onClick={handleContinue}
          >
            Continuar
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SeleccionarTipoImagenAnuncio;
